import { Avatar } from '../primitives/avatar.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { useSelector } from '@tunasong/redux'
import { type HTMLAttributes, type PropsWithChildren, type Ref } from 'react'
import { useNavigate } from 'react-router'
import { getColorCSS, getProfileColor } from '../color/index.js'

const avatarSizePx: Record<string, number> = {
  micro: 20,
  tiny: 24,
  small: 32,
  medium: 40,
  large: 56,
}

type AvatarSize = 'micro' | 'tiny' | 'small' | 'medium' | 'large'

export interface UserAvatarProps extends HTMLAttributes<HTMLDivElement>, PropsWithChildren {
  className?: string
  userId?: string
  size?: AvatarSize
  showName?: boolean
  /** Active link. @default true */
  link?: boolean
  /** Use a picture of the user (if available) */
  picture?: boolean
  /** fallbackName when userId is not set - e.g., before the user is registered */
  fallbackName?: string
  ref?: Ref<HTMLDivElement>
}

export const UserAvatar = ({
  userId,
  className,
  size = 'medium',
  showName = false,
  ref,
  link = true,
  picture = true,
  fallbackName,
  onClick,
  style,
  ...restProps
}: UserAvatarProps) => {
  const loggedInId = useSelector(state => state.user.userId)
  const { profile } = graphHooks.useProfile(userId)
  const navigate = useNavigate()

  const { colorName } = getProfileColor(profile)

  const url = userId === loggedInId ? '/app/profile' : undefined

  const name = profile?.name ?? profile?.email ?? fallbackName
  const showPicture = Boolean(profile?.picture && picture)
  const pictureUrl = showPicture && profile?.picture ? profile.picture : undefined
  const px = avatarSizePx[size]

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (onClick) {
      onClick(event)
      return
    }
    if (link && url) {
      void navigate(url)
    }
  }

  if (!profile) {
    return null
  }

  return (
    <div {...restProps} ref={ref} style={{ display: 'inline-flex', ...style }} onClick={handleClick}>
      <Avatar
        className={className}
        src={pictureUrl}
        alt={name}
        style={{
          width: px,
          height: px,
          backgroundColor: showPicture ? 'transparent' : getColorCSS(colorName),
          ...(link && url ? { cursor: 'pointer' } : {}),
        }}
      >
        {!showPicture && name?.[0].toUpperCase()}
      </Avatar>
      {showName ? (
        <span
          style={{
            marginLeft: 8,
            marginTop: 8,
            marginBottom: 16,
            cursor: 'pointer',
            fontSize: 'var(--tuna-typography-caption-fontSize)',
          }}
        >
          {name}
        </span>
      ) : null}
    </div>
  )
}

export default UserAvatar
